import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Flag from "react-world-flags";

import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Youtube,
  Search,
  Menu,
  X,
  ChevronDown,
} from "lucide-react";

import { navigation } from "../control";

const LOGO = "/assets/images/logo2.png";
const LOGO_TWO = "/assets/images/logo1.png";

const languages = [
  { code: "CN", label: "Chinese" },
  { code: "GB", label: "English" },
  { code: "FR", label: "French" },
  { code: "DE", label: "German" },
  { code: "PT", label: "Portuguese" },
  { code: "ES", label: "Spanish" },
];

const Header = () => {
  const location = useLocation();

  const isPortalPage = location.pathname.startsWith("/portal");

  const [mobileOpen, setMobileOpen] = useState(false);
  const [showHeader, setShowHeader] = useState(true);

  const lastScrollY = useRef(0);

  useEffect(() => {
    if (isPortalPage) {
      setShowHeader(true);
      return;
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= 20) {
        setShowHeader(true);
      } else if (currentScrollY > lastScrollY.current) {
        setShowHeader(false);
      } else {
        setShowHeader(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isPortalPage]);

  const portalMenu = {
    label: "Portal",
    path: "/portal/login",
    children: [
      {
        label: "Portal",
        path: "/portal/login",
      },
      {
        label: "FMS",
        path: "/portal/fms",
      },
    ],
  };

  const cleanedNavigation = navigation.map((item) => {
    if (item.label === "The SPV") {
      return {
        ...item,
        children:
          item.children?.map((child) => {
            if (child.label === "Contact SPV") {
              return {
                ...child,
                path: "https://fzgil.org/",
                external: true,
              };
            }

            return child;
          }) || [],
      };
    }

    return item;
  });

  const hasPortal = cleanedNavigation.some((item) => item.label === "Portal");

  const updatedNavigation = hasPortal
    ? cleanedNavigation
    : [
        ...cleanedNavigation.slice(0, 5),
        portalMenu,
        ...cleanedNavigation.slice(5),
      ];

  const isItemActive = (item) => {
    if (!item.path || item.external) return false;

    if (location.pathname === item.path) return true;

    return item.children?.some((child) => {
      if (!child.path || child.external) return false;

      if (location.pathname === child.path) return true;

      return child.children?.some(
        (subChild) => !subChild.external && location.pathname === subChild.path
      );
    });
  };

  const NavLink = ({ item, className, children, onClick }) => {
    if (item.external) {
      return (
        <a
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
          onClick={onClick}
        >
          {children}
        </a>
      );
    }

    return (
      <Link to={item.path} className={className} onClick={onClick}>
        {children}
      </Link>
    );
  };

  const renderDesktopDropdown = (children) => {
    return (
      <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[10000]">
        <div className="bg-white min-w-[300px] shadow-2xl rounded-md py-3 border-t-[3px] border-[#F2AF11] overflow-visible">
          {children.map((child) => {
            const hasSubChildren = child.children && child.children.length > 0;

            return (
              <div key={child.label} className="relative group/sub">
                <NavLink
                  item={child}
                  className="flex items-center justify-between gap-4 px-6 py-2.5 text-[13px] font-medium text-[#1f2937] hover:text-[#053b14] hover:bg-green-50 transition-colors"
                >
                  <span className="whitespace-pre-line">{child.label}</span>

                  {hasSubChildren && (
                    <span className="font-bold text-[14px]">+</span>
                  )}
                </NavLink>

                {hasSubChildren && (
                  <div className="absolute left-full top-0 pl-2 opacity-0 invisible group-hover/sub:opacity-100 group-hover/sub:visible transition-all duration-200 z-[10001]">
                    <div className="bg-white min-w-[280px] shadow-2xl rounded-md py-3 border-t-[3px] border-[#F2AF11] overflow-hidden">
                      {child.children.map((subChild) => (
                        <NavLink
                          key={subChild.label}
                          item={subChild}
                          className="block px-6 py-2.5 text-[13px] font-medium text-[#1f2937] hover:text-[#053b14] hover:bg-green-50 transition-colors"
                        >
                          {subChild.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMobileChildren = (children) => {
    return (
      <div className="pb-4 pl-4">
        {children.map((child) => {
          const hasSubChildren = child.children && child.children.length > 0;

          return (
            <div key={child.label}>
              <NavLink
                item={child}
                onClick={() => !hasSubChildren && setMobileOpen(false)}
                className="flex items-center justify-between py-2 text-[14px] text-slate-600 hover:text-[#053b14] transition-colors"
              >
                <span className="whitespace-pre-line">{child.label}</span>

                {hasSubChildren && <ChevronDown size={15} />}
              </NavLink>

              {hasSubChildren && (
                <div className="pl-4 pb-2">
                  {child.children.map((subChild) => (
                    <NavLink
                      key={subChild.label}
                      item={subChild}
                      onClick={() => setMobileOpen(false)}
                      className="block py-2 text-[13px] text-slate-500 hover:text-[#053b14] transition-colors"
                    >
                      {subChild.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

 const LanguageFlags = ({ mobile = false }) => (
  <div
    className={
      mobile
        ? "flex items-center gap-1.5 mt-4"
        : "flex items-center gap-1.5 pl-2 border-l border-white/25"
    }
  >
    {languages.map((lang) => (
      <button
        key={lang.code}
        type="button"
        title={lang.label}
        aria-label={lang.label}
        className={`${
          mobile ? "w-5 h-4" : "w-4 h-3"
        } rounded-[1px] overflow-hidden bg-white hover:scale-110 transition-transform`}
      >
        <Flag
          code={lang.code}
          className="w-full h-full object-cover"
        />
      </button>
    ))}
  </div>
);

  return (
    <>
      <header
        className={`${
          isPortalPage ? "relative" : "fixed top-0 left-0 right-0"
        } z-[9999] bg-white shadow-sm transition-transform duration-300 ${
          isPortalPage
            ? "translate-y-0"
            : showHeader
            ? "translate-y-0"
            : "-translate-y-full"
        }`}
      >
        <div className="hidden md:block bg-[#02226a] text-white text-[11px] shadow-md">
          <div className="max-w-[1400px] mx-auto px-5 h-[38px] flex items-center justify-between gap-5">
            <div className="flex items-center gap-5 min-w-0">
              <span className="flex items-center gap-2 hover:text-yellow-100 transition-colors cursor-pointer whitespace-nowrap">
                <Mail size={12} />
                contact@ogfza.gov.ng
              </span>

              <span className="flex items-center gap-2 hover:text-yellow-100 transition-colors cursor-pointer whitespace-nowrap">
                <Phone size={12} />
                +234-909-111-1184
              </span>

              <span className="hidden xl:flex items-center gap-2 hover:text-yellow-100 transition-colors cursor-pointer truncate">
                <MapPin size={12} />
                Marble House, Federal Ocean Terminal, Onne Port Complex, Rivers
                State
              </span>
            </div>

            <div className="flex items-center gap-4 shrink-0">

              <Link
                to="/public-information/public-financial-records"
                className="font-bold uppercase tracking-wide hover:text-yellow-100 transition-colors"
              >
                Public Financial Records
              </Link>

              <Link
                to="/portal/login"
                className="font-bold uppercase tracking-wide hover:text-yellow-100 transition-colors"
              >
                Portal
              </Link>

              <a
                href="https://www.facebook.com/oGFZA"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <Facebook
                  size={13}
                  className="hover:text-yellow-100 transition-colors cursor-pointer"
                />
              </a>

              <a
                href="https://www.twitter.com/OGFZA"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Twitter"
              >
                <Twitter
                  size={13}
                  className="hover:text-yellow-100 transition-colors cursor-pointer"
                />
              </a>

              <a
                href="https://ng.linkedin.com/company/ogfza"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <Linkedin
                  size={13}
                  className="hover:text-yellow-100 transition-colors cursor-pointer"
                />
              </a>

              <a
                href="https://instagram.com/OGFZA"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <Instagram
                  size={13}
                  className="hover:text-yellow-100 transition-colors cursor-pointer"
                />
              </a>

              <a
                href="https://www.youtube.com/@OGFZALive"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
              >
                <Youtube
                  size={15}
                  className="hover:text-yellow-100 transition-colors cursor-pointer"
                />
              </a>

              <LanguageFlags />
            </div>
          </div>
        </div>

        <div className="bg-white">
          <div className="max-w-[1400px] mx-auto px-3 md:px-5 h-[90px] md:h-[104px] flex items-center justify-between relative">
            <Link
              to="/"
              className="flex items-center justify-start gap-3 md:gap-4 min-w-0 max-w-[calc(100%-60px)] lg:max-w-[calc(100%-170px)]"
            >
              <div className="flex items-center gap-3 shrink-0">
                <div className="hidden md:flex items-center gap-3">
                  <img
                    src={LOGO_TWO}
                    alt="OGFZA Logo Two"
                    className="w-[72px] h-[72px] object-contain"
                  />

                  <span className="h-[58px] w-[2px] bg-gradient-to-b from-[#020f2e] via-[#053b14] to-[#F2AF11]" />

                  <img
                    src={LOGO}
                    alt="Oil & Gas Free Zones Authority"
                    className="w-[72px] h-[72px] object-contain"
                  />
                </div>

                <div className="md:hidden">
                  <img
                    src={LOGO_TWO}
                    alt="OGFZA Logo"
                    className="w-[58px] h-[58px] object-contain"
                  />
                </div>
              </div>

              <div className="flex flex-col min-w-0">
                <span className="text-[12px] sm:text-[13px] md:text-[14px] font-bold uppercase tracking-[1.5px] text-[#F2AF11] leading-none">
                  Federal Republic of Nigeria
                </span>

                <h1 className="text-left font-black leading-tight tracking-[-0.7px]">
                  <span className="block md:hidden text-[28px] sm:text-[34px] leading-none bg-gradient-to-r from-[#020f2e] via-[#053b14] via-[72%] to-[#F2AF11] bg-clip-text text-transparent">
                    OGFZA
                  </span>

                  <span className="hidden md:block text-[26px] lg:text-[34px] xl:text-[37px] bg-gradient-to-r from-[#020f2e] via-[#053b14] via-[72%] to-[#F2AF11] bg-clip-text text-transparent whitespace-nowrap">
                    Oil & Gas Free Zones Authority
                  </span>
                </h1>

                <span className="text-[11px] md:text-[12px] xl:text-[13px] text-slate-500 font-medium tracking-wide mt-1 truncate">
                  Promoting Investment • Regulating Free Zones • Driving
                  Economic Growth
                </span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center">
              <a
                href="https://www.pebec.gov.ng/reportgov-ng"
                target="_blank"
                rel="noopener noreferrer"
                className="h-[px] px-6 rounded-tl-[12px] rounded-br-[12px] bg-[#ff0000] text-white hover:bg-[#1f9d4c] hover:text-[#ffffff] shadow-xl flex items-center justify-center font-black text-[20px] uppercase tracking-[-0.5px] transition-all duration-300"
                style={{
                  fontFamily: "'Alegreya Sans', sans-serif",
                }}
              >
                Report.Gov
              </a>
            </div>

            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden absolute right-3 w-11 h-11 flex items-center justify-center rounded-xl border border-slate-200 hover:border-[#053b14] hover:text-[#053b14] transition-colors"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>

        <div className="hidden lg:block bg-gradient-to-r from-[#020f2e] via-[#053b14] via-[72%] to-[#F2AF11] shadow-xl">
          <div className="max-w-[1400px] mx-auto px-5">
            <div className="h-[58px] flex items-center justify-center relative">
              <nav className="flex items-center gap-4 xl:gap-5 2xl:gap-7">
                {updatedNavigation.map((item) => {
                  const hasChildren = item.children && item.children.length > 0;
                  const isActive = isItemActive(item);

                  return (
                    <div key={item.label} className="relative group">
                      <NavLink
                        item={item}
                        className={`flex items-center gap-1 text-[12.5px] xl:text-[13.5px] font-bold uppercase tracking-[0.35px] whitespace-nowrap transition-all duration-300 ${
                          isActive
                            ? "text-yellow-100"
                            : "text-white hover:text-yellow-100"
                        }`}
                      >
                        {item.label}

                        {hasChildren && (
                          <span className="font-bold text-[14px] ml-0.5">
                            +
                          </span>
                        )}
                      </NavLink>

                      {hasChildren && renderDesktopDropdown(item.children)}
                    </div>
                  );
                })}
              </nav>

              <button
                className="absolute right-0 text-white hover:text-yellow-100 transition-colors"
                aria-label="Search"
              >
                <Search size={18} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {!isPortalPage && <div className="h-[90px] md:h-[200px]" />}

      <div
        className={`fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300 lg:hidden ${
          mobileOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setMobileOpen(false)}
      />

      <div
        className={`fixed top-0 left-0 h-full w-[320px] max-w-[85vw] bg-white z-[9999] shadow-2xl transition-transform duration-300 overflow-y-auto lg:hidden ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b bg-gradient-to-r from-[#020f2e] via-[#053b14] via-[72%] to-[#F2AF11]">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_TWO}
              alt="OGFZA Logo"
              className="w-[58px] h-[58px] object-contain bg-white rounded-lg p-1"
            />
          </div>

          <button
            onClick={() => setMobileOpen(false)}
            className="w-10 h-10 rounded-lg border border-white/30 text-white flex items-center justify-center"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <div className="px-5 py-5 border-b bg-white">
          <h2 className="text-[34px] font-black leading-none tracking-[-1px] bg-gradient-to-r from-[#020f2e] via-[#053b14] via-[72%] to-[#F2AF11] bg-clip-text text-transparent">
            OGFZA
          </h2>

          <p className="text-[12px] text-slate-500 mt-2 font-medium">
            Oil & Gas Free Zones Authority
          </p>

          <LanguageFlags mobile />
        </div>

        <div className="px-5 py-5 border-b bg-white">
          <a
            href="https://www.pebec.gov.ng/reportgov-ng"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="w-full h-[52px] rounded-tl-[12px] rounded-br-[12px] bg-[#b80000] text-white hover:bg-[#F2AF11] hover:text-[#020f2e] shadow-xl flex items-center justify-center font-black text-[20px] uppercase transition-all duration-300"
            style={{
              fontFamily: "'Alegreya Sans', sans-serif",
            }}
          >
            Report.Gov
          </a>
        </div>

        <nav className="px-5 py-5">
          {updatedNavigation.map((item) => {
            const hasChildren = item.children && item.children.length > 0;

            return (
              <div key={item.label} className="border-b border-slate-100">
                <NavLink
                  item={item}
                  onClick={() => !hasChildren && setMobileOpen(false)}
                  className="flex items-center justify-between py-4 text-[16px] font-semibold text-slate-800 hover:text-[#053b14] transition-colors"
                >
                  <span>{item.label}</span>

                  {hasChildren && <ChevronDown size={17} />}
                </NavLink>

                {hasChildren && renderMobileChildren(item.children)}
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
};

export default Header;